import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "react-bootstrap";
import { toast } from "react-toastify";
import CommonHeadingNBtns from "../common-components/CommonHeadingNBtns";
import { showConfirmAlert } from "../components-react/ReactAlert";
import {
  fetchMemberSubscriptionData,
  fetchPtUpgradeAddonsData,
} from "../../redux/plans-reducers/plansSlice";
import { commonPostPutApi } from "../../redux/reducers/commonApiSlice";
import { post_upgrade_pt_membership } from "../../data/ApiEndpoints";
import SelectDropdown from "../components-react/SelectDropdown";
import { convertThenFormat, formatDateTimezon } from "../common-data/Converts";
import { getSubsProgress } from "../pt/pt-flow-new/users-info/calculations";
import Loader from "../ui/Loader";
import { selectGymData } from "../../redux/reducers/gymDataSlice";
import "../../styles/pt/pt-transfer.css";

const validateForm = (formValues) => {
  let errors = {};
  const mobileRegex = /^\d{10}$/;

  if (!formValues.memberMobile) {
    errors.memberMobile = "Member Number Required!";
  } else if (!mobileRegex.test(formValues.memberMobile)) {
    errors.memberMobile = "invalid mobile number!";
  }

  if (!formValues.subscriptionId) {
    errors.subscriptionId = "Required!";
  }

  if (!formValues.selectedAddon) {
    errors.selectedAddon = "Required!";
  }

  if (!formValues.selectedSession) {
    errors.selectedSession = "Required!";
  }

  if (!formValues.planPrice) {
    errors.planPrice = "Required!";
  }

  return errors;
};

const UPTMForm = ({ userInfoData, callFunction, closeModal }) => {
  const dispatch = useDispatch();
  const gymData = useSelector(selectGymData);
  const { postPutLoading } = useSelector((state) => state.common);
  const {
    memberSubscription,
    memberSubscriptionLoading,
    memberSubscriptionError,
    ptUpgradeAddons,
    ptUpgradeAddonsLoading,
  } = useSelector((state) => state.plans);

  const formRef = useRef(null);

  const [isFetchUser, setIsFetchUser] = useState(false);
  const [memberFetchedData, setMemberFetchedData] = useState(null);
  const iniMemStatus = {
    isShowForm: false,
    errorMessage: "",
  };
  const [memberStatus, setMemberStatus] = useState(iniMemStatus);

  const iniFormValues = {
    subscriptionId: "",
    memberMobile: "",
    memberName: "",
    memberEmail: "",
    userId: "",
    selectedAddon: "",
    selectedSession: "",
    planPrice: "",
    remarks: "",
  };
  const [formValues, setFormValues] = useState(iniFormValues);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmit, setIsSubmit] = useState(false);

  const onChangeInput = (e) => {
    const { name, value, type, checked } = e.target;
    const file = type === "file" ? e.target.files[0] : null;

    setIsSubmit(false);

    setFormValues((prevs) => ({
      ...prevs,
      [name]: type === "checkbox" ? checked : type === "file" ? file : value,
    }));
  };

  const onChangeDropdown = (name, value) => {
    setIsSubmit(false);

    setFormValues((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "selectedAddon") {
        next.selectedSession = "";
      }
      return next;
    });
  };

  const handleSelect = (subsId) => {
    if (subsId !== formValues.subscriptionId) {
      setFormValues((prevs) => ({
        ...prevs,
        subscriptionId: subsId,
        selectedAddon: "",
        selectedSession: "",
        planPrice: "",
      }));
      formRef?.current?.scrollIntoView({ behavior: "smooth" });
    } else {
      setFormValues((prevs) => ({
        ...prevs,
        subscriptionId: "",
        selectedAddon: "",
        selectedSession: "",
        planPrice: "",
      }));
    }
  };

  const onSumitForm = (e) => {
    e.preventDefault();
    setFormErrors(validateForm(formValues));
    setIsSubmit(true);
  };

  function getMemberDetails(filters) {
    dispatch(
      fetchMemberSubscriptionData({
        filters: filters,
        token: userInfoData?.token,
      })
    );
  }

  function onClickFetchUserDetails(e) {
    e.preventDefault();

    if (formValues.memberMobile && !formErrors.memberMobile) {
      const filters = {
        mobile_number: formValues.memberMobile,
        type: "addon_pt",
      };

      setIsFetchUser(true);
      getMemberDetails(filters);
    }
  }

  function onClearNumber(e) {
    e && e.preventDefault();

    const handleConfirm = () => {
      setFormValues(iniFormValues);
      setIsSubmit(false);
      setIsFetchUser(false);
      setMemberFetchedData(null);
    };

    showConfirmAlert(handleConfirm, `You want to clear the form?`);
  }

  const handleSubmitForm = async (payload) => {
    const method = "POST";
    const endpoint = post_upgrade_pt_membership;
    const token = userInfoData?.token || "";

    try {
      const result = await dispatch(
        commonPostPutApi({ method, endpoint, payload, token })
      );

      if (result?.payload?.status) {
        setIsSubmit(false);
        setFormValues(iniFormValues);
        setIsFetchUser(false);
        setMemberFetchedData(null);
        callFunction();
        closeModal();
        toast.success(result?.payload?.message || "Success!");
      } else {
        const errorMessage =
          result?.payload?.data ||
          result?.payload?.message ||
          result?.error?.message;
        toast.error(errorMessage || "Something Went Wrong!");
      }
    } catch (error) {
      console.error("Error making POST request:", error);
    }
  };

  useEffect(() => {
    if (
      isFetchUser &&
      !memberSubscriptionLoading &&
      !formErrors.memberMobile &&
      !memberSubscriptionError &&
      memberSubscription?.status &&
      memberSubscription?.data?.length > 0
    ) {
      const memberData = memberSubscription.data;
      const { gym_name, gym_id, user_id, user_name, user_email } =
        memberSubscription.data[0];

      setFormValues((prevs) => ({
        ...prevs,
        userId: user_id,
        memberName: user_name,
        memberEmail: user_email,
        selectedGym: {
          label: gym_name,
          value: gym_id,
        },
      }));

      setMemberFetchedData(memberData);
      setMemberStatus({
        isShowForm: true,
        errorMessage: "",
      });
    } else {
      setFormValues((prevs) => ({
        ...prevs,
        userId: "",
        memberName: "",
        memberEmail: "",
        selectedGym: "",
      }));
      setMemberFetchedData(null);
      setMemberStatus(iniMemStatus);
      return;
    }
  }, [
    isFetchUser,
    memberSubscriptionLoading,
    memberSubscriptionError,
    formErrors.memberMobile,
  ]);

  useEffect(() => {
    if (formValues.selectedGym?.value) {
      dispatch(
        fetchPtUpgradeAddonsData({
          filters: { gym_id: formValues.selectedGym.value },
          token: userInfoData?.token,
        })
      );
    }
  }, [formValues.selectedGym]);

  const addonOptions = useMemo(() => {
    const rows = ptUpgradeAddons?.data || [];
    return rows.map((addon) => ({
      label: addon.addon_name,
      value: addon.addon_uid,
      sessions: addon.sessions || [],
    }));
  }, [ptUpgradeAddons]);

  const sessionOptions = useMemo(() => {
    const sessions = formValues.selectedAddon?.sessions || [];
    return sessions.map((s) => ({
      label: `${s.n_session} sessions / ${s.duration || 0} days / ₹${s.price || 0}`,
      value: s.session_uid,
    }));
  }, [formValues.selectedAddon]);

  useEffect(() => {
    if (isSubmit && Object.keys(formErrors)?.length === 0) {
      const userData = {
        subscription_id: formValues.subscriptionId || "",
        upgrade_session_id: formValues.selectedSession?.value || "",
        upgrade_amount: formValues.planPrice || "",
        upgrade_description: formValues.remarks || "",
      };

      const handleConfirm = async () => {
        handleSubmitForm(userData);
      };

      showConfirmAlert(
        handleConfirm,
        `Are you sure!`,
        "Edit Form",
        "Submit Form"
      );
    }
  }, [isSubmit, formErrors]);

  return (
    <div className="form-container">
      <CommonHeadingNBtns
        title={`Upgrade PT Membership`}
        onClose={closeModal}
        onClick={onSumitForm}
        isLoading={postPutLoading}
        isDisabled={!memberStatus.isShowForm}
      />

      <form className="formContainer" style={{ marginTop: "30px" }}>
        <div className="formInputsGrid twoGridSystem input-with-btn">
          <div className="formInputBox">
            <label htmlFor="memberMobile" className={`formInputLabel`}>
              member mobile <span className="required">*</span>
            </label>

            <input
              type="number"
              placeholder="Enter Member Mobile Number"
              className={`formInput ${formErrors?.memberMobile ? "formError" : ""}`}
              name="memberMobile"
              value={formValues.memberMobile}
              onChange={onChangeInput}
            />

            <span className="error-label">
              {formErrors?.memberMobile || ""}
            </span>

            {!memberStatus.isShowForm && memberStatus.errorMessage && (
              <span className="info">{memberStatus.errorMessage || ""}</span>
            )}
          </div>

          <div className="formInputButtons">
            <Button
              type="button"
              className="saveBtn"
              variant="danger"
              onClick={onClickFetchUserDetails}
              disabled={memberFetchedData || formErrors.memberMobile}
            >
              {memberSubscriptionLoading ? <Loader /> : "Fetch Member Details"}
            </Button>
            {!memberSubscriptionLoading && (
              <Button
                type="button"
                className="cancelBtn"
                variant="dark"
                onClick={onClearNumber}
              >
                Clear
              </Button>
            )}
          </div>
        </div>

        {memberStatus.isShowForm && (
          <>
            <div className="formInputsGrid fourGridSystem">
              <div className="formInputBox">
                <label htmlFor="memberName" className={`formInputLabel`}>
                  member Name
                </label>

                <input
                  type="text"
                  placeholder="Enter Member Full Name"
                  className={`formInput ${formErrors?.memberName ? "formError" : ""}`}
                  name="memberName"
                  value={formValues.memberName}
                  onChange={onChangeInput}
                  disabled
                />
              </div>

              <div className="formInputBox">
                <label htmlFor="memberMobile" className={`formInputLabel`}>
                  member mobile
                </label>

                <input
                  type="email"
                  placeholder="Enter Member Mobile"
                  className={`formInput ${formErrors?.memberMobile ? "formError" : ""}`}
                  name="memberMobile"
                  value={formValues.memberMobile}
                  onChange={onChangeInput}
                  disabled
                />
              </div>

              <div className="formInputBox">
                <label htmlFor="memberEmail" className={`formInputLabel`}>
                  member email
                </label>

                <input
                  type="email"
                  placeholder="Enter Member Email"
                  className={`formInput ${formErrors?.memberEmail ? "formError" : ""}`}
                  name="memberEmail"
                  value={formValues.memberEmail}
                  onChange={onChangeInput}
                  disabled
                />
              </div>

              <div className="formInputBox">
                <label htmlFor="selectedGym" className={`formInputLabel`}>
                  gym name
                </label>

                <div
                  className={`commonWrapper disabled ${formErrors.selectedGym ? "formError" : ""}`}
                >
                  <SelectDropdown
                    options={gymData?.length > 0 ? gymData : []}
                    selectedOption={formValues.selectedGym}
                    setSelectedOption={(selectedValue) =>
                      onChangeDropdown("selectedGym", selectedValue)
                    }
                    name="Gym"
                    isDisable={true}
                  />
                </div>
              </div>
            </div>

            <div className="membership-details plan-details grid-box-clickables">
              {memberFetchedData?.map((item, index) => {
                const {
                  type,
                  plan_name,
                  addon_name,
                  subscription_status,
                  trainer_name,
                  sub_added_by_name,
                  sub_added_by_mobile,
                  sub_added_by_email,
                  price,
                  start_date,
                  expire_date,
                  completed_session,
                  n_session,
                  uid,
                } = item || {};

                const planName =
                  type === "regular"
                    ? plan_name
                    : type === "addon_pt"
                    ? addon_name
                    : type;

                const isActive = subscription_status === "active";
                const planStatusBg = isActive ? "#09BA09" : "#BA0934";

                const startDate = start_date ? formatDateTimezon(start_date) : "-";
                const endDate = expire_date ? formatDateTimezon(expire_date) : "-";

                const daysLeft = getSubsProgress(start_date, expire_date).daysLeft;

                const isCardSelected = uid === formValues.subscriptionId;

                return (
                  <div
                    className={`membership-plans ${isCardSelected ? "active" : ""} ${formErrors?.subscriptionId ? "formError" : ""}`}
                    key={index}
                  >
                    <div className="plan-col icon">
                      <div className="plan-name-and-status flexBetween">
                        <h3 className="plan-name">{planName || "NA"}</h3>
                        <span
                          className="plan-status"
                          style={{ background: planStatusBg }}
                        >
                          {subscription_status || "NA"}
                        </span>
                      </div>
                    </div>

                    <div className="plan-col details">
                      <div className="info-label-and-value">
                        <span className="label">trainer name:</span>
                        <span className="value">{trainer_name || "NA"}</span>
                      </div>
                      <div className="info-label-and-value">
                        <span className="label">Amount Paid:</span>
                        <span className="value">
                          ₹{convertThenFormat(price || 0)}
                        </span>
                      </div>
                      <div className="info-label-and-value">
                        <span className="label">Plan Duration:</span>
                        <span className="value">
                          {startDate} - {endDate}
                        </span>
                      </div>
                      <div className="info-label-and-value">
                        <span className="label">Session Status:</span>
                        <span className="value">
                          {completed_session || 0} / {n_session || 0}
                        </span>
                      </div>
                      <div className="info-label-and-value">
                        <span className="label">Days Left:</span>
                        <span
                          className="value"
                          style={{
                            padding: "2px 5px",
                            background: "#0A4CCC",
                            color: "#fff",
                            borderRadius: "5px",
                          }}
                        >
                          {daysLeft || 0}
                        </span>
                      </div>
                      <div className="info-label-and-value">
                        <span className="label">subscription added by:</span>
                        <span className="value">
                          {sub_added_by_name || "NA"}
                          <br />
                          {(sub_added_by_mobile || sub_added_by_email) && (
                            <small className="lowercase">
                              {sub_added_by_mobile || ""}
                              <br />
                              {sub_added_by_email || ""}
                            </small>
                          )}
                        </span>
                      </div>
                    </div>

                    <div className="plan-col btns">
                      <Button
                        type="button"
                        className="saveBtn"
                        variant="danger"
                        onClick={() => handleSelect(uid)}
                        disabled={isActive ? false : true}
                        style={{
                          cursor: isActive ? "pointer" : "not-allowed",
                          background: isCardSelected ? "#00712d" : "",
                          border: "none",
                          textTransform: "uppercase",
                        }}
                      >
                        {isCardSelected ? "selected" : "select to upgrade"}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div
              className="formInputsGrid fourGridSystem"
              style={{ rowGap: "1rem" }}
              ref={formRef}
            >
              {formValues.subscriptionId && (
                <>
                  <div className="formInputBox span2">
                    <label htmlFor="selectedAddon" className={`formInputLabel`}>
                      select PT to upgrade <span className="required">*</span>
                    </label>

                    <div
                      className={`commonWrapper ${formErrors.selectedAddon ? "formError" : ""}`}
                    >
                      <SelectDropdown
                        options={addonOptions}
                        selectedOption={formValues.selectedAddon}
                        setSelectedOption={(selectedValue) =>
                          onChangeDropdown("selectedAddon", selectedValue)
                        }
                        name="PT Addon"
                        isDisable={ptUpgradeAddonsLoading}
                      />
                    </div>

                    <span className="error-label">
                      {formErrors?.selectedAddon || ""}
                    </span>
                  </div>

                  <div className="formInputBox span2">
                    <label htmlFor="selectedSession" className={`formInputLabel`}>
                      select session <span className="required">*</span>
                    </label>

                    <div
                      className={`commonWrapper ${formErrors.selectedSession ? "formError" : ""}`}
                    >
                      <SelectDropdown
                        options={sessionOptions}
                        selectedOption={formValues.selectedSession}
                        setSelectedOption={(selectedValue) =>
                          onChangeDropdown("selectedSession", selectedValue)
                        }
                        name="Session"
                        isDisable={!formValues.selectedAddon}
                      />
                    </div>

                    <span className="error-label">
                      {formErrors?.selectedSession || ""}
                    </span>
                  </div>

                  <div className="formInputBox">
                    <label htmlFor="planPrice" className={`formInputLabel`}>
                      Upgrade Amount <span className="required">*</span>
                    </label>

                    <input
                      type="number"
                      placeholder="Enter Amount"
                      className={`formInput ${formErrors?.planPrice ? "formError" : ""}`}
                      name="planPrice"
                      value={formValues.planPrice}
                      onChange={onChangeInput}
                    />

                    <span className="error-label">
                      {formErrors?.planPrice || ""}
                    </span>
                  </div>

                  <div className="formInputBox span2">
                    <label htmlFor="remarks" className="formInputLabel">
                      remarks <span className="not required">(Optional)</span>
                    </label>

                    <textarea
                      name="remarks"
                      className={`formInput ${formErrors.remarks && "formError"}`}
                      style={{ minHeight: "60px" }}
                      placeholder="Enter PT Upgrade Reason"
                      value={formValues.remarks}
                      onChange={onChangeInput}
                    ></textarea>

                    <span className="error-label">
                      {formErrors?.remarks || ""}
                    </span>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </form>
    </div>
  );
};

export default UPTMForm;
